import { Link } from "react-router-dom";
import { ArrowUpRight, Bell, MapPin } from "lucide-react";
import Reveal from "@/components/Reveal";
import { DOORDASH_URL, UBEREATS_URL, GRUBHUB_URL } from "@/lib/data";

const DoorDashLogo = (props) => (
  <svg viewBox="0 0 122 16" fill="currentColor" {...props}>
    <path d="M120.7 2.6A4.1 4.1 0 00116.6.5h-8.2v15h3v-5.5h5.2c2.2 0 4.1-1.8 4.1-4v-.7c0-1-.4-1.9-1-2.7zm-2.4 3.2c0 .8-.7 1.5-1.5 1.5h-5.4V3.4h5.4c.8 0 1.5.7 1.5 1.5v.9z" />
  </svg>
);

function Platform({ name, blurb, url, accent, testid }) {
  const ready = Boolean(url);
  return (
    <Reveal className="group bg-[#1A1A1A] border border-[#F5ECD7]/5 rounded-2xl p-7 md:p-9 flex flex-col gap-5 transition-colors hover:border-[#C99C3A]/40">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-3xl md:text-4xl tracking-tight text-[#F5ECD7]">{name}</h3>
          <p className="mt-2 text-[#F5ECD7]/65 text-sm leading-relaxed max-w-md">{blurb}</p>
        </div>
        <span
          className="text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 border"
          style={{
            borderColor: ready ? accent : "#9B2226",
            color: ready ? accent : "#9B2226",
          }}
        >
          {ready ? "Live" : "Coming Soon"}
        </span>
      </div>

      {ready ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          data-testid={`${testid}-link`}
          className="btn-gold self-start"
        >
          Order on {name} <ArrowUpRight size={14} />
        </a>
      ) : (
        <button
          disabled
          data-testid={`${testid}-soon`}
          className="btn-outline self-start opacity-60 cursor-not-allowed"
        >
          <Bell size={14} /> Launching Soon
        </button>
      )}
    </Reveal>
  );
}

export default function Order() {
  return (
    <div data-testid="order-page" className="bg-[#121212]">
      {/* Hero */}
      <section className="pt-20 md:pt-28 pb-12">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C99C3A] mb-5">Order Online</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.98] text-[#F5ECD7]">
            Tacos to your{" "}
            <span className="font-serif-italic font-normal text-[#C99C3A]">door</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-[#F5ECD7]/65 text-lg">
            We're rolling out delivery soon. The truck stays where the
            community is — but you'll be able to get our deep-fried tacos
            delivered straight to your front porch.
          </p>
        </div>
      </section>

      {/* Platforms */}
      <section className="section-pad pt-0">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Platform
            name="DoorDash"
            blurb="Our primary delivery partner. Full menu, fast delivery across Charlotte."
            url={DOORDASH_URL}
            accent="#FF3008"
            testid="doordash"
          />
          <Platform
            name="Uber Eats"
            blurb="Coming next. Tap notify and we'll let you know the second we go live."
            url={UBEREATS_URL}
            accent="#06C167"
            testid="ubereats"
          />
          <Platform
            name="Grubhub"
            blurb="Rolling out as we expand. Order on Grubhub once your zip is covered."
            url={GRUBHUB_URL}
            accent="#FF8000"
            testid="grubhub"
          />
        </div>
      </section>

      {/* In-Person CTA */}
      <section className="section-pad pt-0">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="bg-[#F5ECD7] text-[#121212] rounded-2xl p-8 md:p-14 grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <p className="text-xs uppercase tracking-[0.3em] text-[#9B2226] mb-4">
                Prefer to grab it hot?
              </p>
              <h2 className="font-display text-3xl md:text-5xl tracking-tighter leading-tight">
                Find us tonight.
                <br />
                <span className="font-serif-italic font-normal">Order fresh off the flat-top.</span>
              </h2>
              <p className="mt-4 text-[#121212]/65 max-w-xl">
                Our weekly schedule rotates breweries, festivals, and pop-ups
                all over Charlotte. Order in person — crispy is always crispier.
              </p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <Link
                to="/schedule"
                data-testid="order-find-truck"
                className="btn-red w-full md:w-auto justify-center"
              >
                <MapPin size={14} /> Find the Truck
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
